import React, { forwardRef, useCallback, useMemo } from "react";
import styled from "styled-components";

import * as flomoji from "@floji-internal/noto-emoji";

import { Strings } from "--root/strings/en";

import { Button } from "--floponents-web/atoms/Button";
import { Body2 } from "--components/typography";

import EmojiButton from "../components/EmojiButton";

const StyledEmojiPickerCategory = styled.div`
    overflow-x: hidden;
`;

const EmojiPickerCategoryClearButton = styled(Button).attrs(() => ({
    size: "small",
    variant: "text"
}))`
    opacity: 0;
    transition: opacity ${props => props.theme.transitions.short};
    visibility: hidden;
`;

const EmojiPickerCategoryTitle = styled.div`
    align-items: baseline;
    background: ${props => props.theme.palette.surface};
    display: flex;
    gap: ${props => props.theme.spacing(1)};
    padding: ${props => props.theme.spacing(0, 0, 0.5, 0)};    
    position: sticky;
    top: 0;
    z-index: 1;

    :hover {
        ${EmojiPickerCategoryClearButton} {
            opacity: 1;
            visibility: visible;
        }
    }
`;

const EmojiPickerCategoryGrid = styled.div`
    display: flex;
    flex-flow: row wrap;
`;

interface EmojiPickerCategoryProps {
    /** Emoji to display in category. */
    emoji: flomoji.tPickerEmoji[];
    /** Array of currently starred emoji. */
    starredEmoji?: flomoji.tPickerEmoji[];
    /** Emoji category name. */
    category?: flomoji.tEmojiCategory;
    /** Active tone. */
    tone?: flomoji.tSkinTone;
    /** Custom title (with fallback to category title). */
    customTitle?: string;
    /** Whether category is user-clearable (recents/favourites). */
    isClearable?: boolean;

    /** Called when category is cleared by user (if applicable). */
    onCleared?: () => void;
    /**
     * Called with emoji when highlighted. If the supplied emoji is undefined,
     * no emoji is highlighted.
     */
    onEmojiHighlighted?: (emoji?: flomoji.tPickerEmoji) => void;
    /** Called with emoji when picked. */
    onEmojiPicked?: (emoji: flomoji.tPickerEmoji) => void;
    /** Called with emoji when starred. */
    onEmojiStarred?: (emoji: flomoji.tPickerEmoji) => void;
    /** Called with emoji when unstarred. */
    onEmojiUnstarred?: (emoji: flomoji.tPickerEmoji) => void;
}
/**
 * Shows grid of clickable emoji (passed in props) and provides callbacks for
 * user interaction with each emoji.
 */
const EmojiPickerCategory = forwardRef<
    HTMLDivElement, EmojiPickerCategoryProps
>((props, ref) => {
    const {
        onEmojiHighlighted, onEmojiPicked, onEmojiStarred, onEmojiUnstarred
    } = props;

    const handleEmojiMouseLeave = useCallback(() => {
        onEmojiHighlighted?.(undefined);
    }, [onEmojiHighlighted]);

    const categoryTitle = useMemo(() => (
        props.customTitle ??
        (props.category && flomoji.categories.get(props.category)?.en)
    ), [props.category, props.customTitle]);

    const emojiList = useMemo(() => props.emoji.map(emoji => (
        <EmojiButton
            key={emoji.emoji}
            emoji={emoji}
            tone={props.tone}
            isStarred={props.starredEmoji?.includes(emoji)}
            onMouseEnter={() => onEmojiHighlighted?.(emoji)}
            onMouseLeave={handleEmojiMouseLeave}
            onClick={() => onEmojiPicked?.(emoji)}
            onStarred={() => onEmojiStarred?.(emoji)}
            onUnstarred={() => onEmojiUnstarred?.(emoji)}
        />
    )), [
        handleEmojiMouseLeave, onEmojiHighlighted, onEmojiPicked,
        onEmojiStarred, onEmojiUnstarred,
        props.emoji, props.starredEmoji, props.tone
    ]);

    return (
        <StyledEmojiPickerCategory ref={ref}>
            {categoryTitle && (
                <EmojiPickerCategoryTitle>
                    <Body2>{categoryTitle}</Body2>
                    {props.isClearable && (
                        <EmojiPickerCategoryClearButton onClick={props.onCleared}>
                            {Strings.emojiPicker.clear}
                        </EmojiPickerCategoryClearButton>
                    )}
                </EmojiPickerCategoryTitle>
            )}
            <EmojiPickerCategoryGrid>
                {emojiList}
            </EmojiPickerCategoryGrid>
        </StyledEmojiPickerCategory>
    );
});

EmojiPickerCategory.displayName = EmojiPickerCategory.name;

export default EmojiPickerCategory;
